'use client';

import {
	Portal, Select, ListCollection,
} from '@chakra-ui/react';
import type { SelectRootProps } from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';

import type { FormBaseInputProps, FormValues } from '../../../types';
import { FormFloatingField } from '../../wrapper';

export type SelectInputProps<T> = FormBaseInputProps<T> & {
	clearTrigger?: boolean
	placeholder?:  string

	collection: ListCollection<{
		label: string
		value: string
	}>
} & SelectRootProps;

function normalizeSelectValue(value: unknown): string[] {
	if (Array.isArray(value)) {
		return value.map(v => (typeof v === 'string' ? v : String(v ?? '')));
	}

	if (value == null || value === '') {
		return [];
	}

	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return [ String(value) ];
	}

	return [];
}

export const SelectInput = <T extends FormValues>({
	clearTrigger = false,
	collection,
	helperText,
	label,
	multiple,
	name,
	optionalText,
	placeholder = 'Select an option',
	required,
	...selectRootProps
}: SelectInputProps<T>) => {
	const { control } = useFormContext<T>();

	return (
		<FormFloatingField
			helperText={helperText}
			label={label}
			name={name}
			optionalText={optionalText}
			required={required}
		>
			<Controller
				control={control}
				name={name}
				render={({ field }) => {
					const uiValue = normalizeSelectValue(field.value);

					const handleChange = ({ value }: { value: string[] }) => {
						if (multiple) field.onChange(value ?? []);
						else field.onChange(value?.[0] ?? '');
					};

					return (
						<Select.Root
							collection={collection}
							multiple={multiple}
							name={field.name}
							onInteractOutside={() => field.onBlur()}
							onValueChange={handleChange}
							value={uiValue}
							{...selectRootProps}
						>
							<Select.HiddenSelect />
							<Select.Control>
								<Select.Trigger>
									<Select.ValueText placeholder={placeholder} />
								</Select.Trigger>
								<Select.IndicatorGroup>
									{clearTrigger && <Select.ClearTrigger />}
									<Select.Indicator />
								</Select.IndicatorGroup>
							</Select.Control>
							<Portal>
								<Select.Positioner>
									<Select.Content>
										{collection?.items?.map(item => (
											<Select.Item item={item} key={item.value}>
												{item.label}
												<Select.ItemIndicator />
											</Select.Item>
										))}
									</Select.Content>
								</Select.Positioner>
							</Portal>
						</Select.Root>
					);
				}}
			/>
		</FormFloatingField>
	);
};
